/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */
import {
  Calculator,
  CheckCircle,
  Clock,
  DollarSign,
  Droplets,
  FileText,
  Layers,
  Ruler,
  Truck,
  Wrench,
  X,
} from "lucide-react";
import type { SolutionTemplateConfig } from "@/components/landing/solution-template";
import {
  CTA,
  AI_TOOL_COUNT,
  STARTING_PRICE,
} from "@/content/landing/constants";

export const plumbingConfig: SolutionTemplateConfig = {
  tradeName: "Plumbing",
  tradeSlug: "plumbing",
  hero: {
    badge: "Plumbing Business Platform",
    title: "Software for plumbing contractors",
    titleHighlight: "from rough-in to final bill.",
    description:
      "What did that rough-in actually cost? Are your service calls profitable? Is copper vs PEX changing your margins? One platform for your costs, estimates, projects, service work, and invoices.",
    ctaText: CTA.PRIMARY,
    trustIndicators: [
      "7-day free trial",
      "No long-term contracts",
      "Pre-loaded plumbing costs",
      "Cancel anytime",
    ],
  },
  trustBar: {
    headline: "Built for plumbing contractors",
    proofPoints: [
      { icon: Droplets, text: "New construction, remodel, and service" },
      { icon: DollarSign, text: "Material pricing by type and diameter" },
      { icon: Wrench, text: "Service calls with visit tracking" },
    ],
  },
  painPoints: {
    title: "Does this sound familiar?",
    description:
      "The daily reality of running a plumbing business without a system.",
    items: [
      {
        icon: Clock,
        title: "Every joint is a cost",
        description:
          "Copper fittings, PEX fittings, CPVC fittings — different prices for each material, each diameter, each connection type. You're pricing from memory because no spreadsheet keeps up.",
      },
      {
        icon: DollarSign,
        title: "Service calls are half your business",
        description:
          "Water heater replacements, drain clearing, leak repairs — service work is steady revenue but you're tracking it on a notepad. No idea which types of calls are actually profitable.",
      },
      {
        icon: FileText,
        title: "Fixture counts scattered across the plan set",
        description:
          "Sinks, toilets, tubs, water heaters across 12 pages of plans. Count them once on paper, price them in a spreadsheet, re-enter them for the proposal. Three passes on the same data.",
      },
    ],
  },
  stats: [
    { value: "Projects + Service", label: "Both in one system" },
    { value: `${AI_TOOL_COUNT} tools`, label: "AI assistant for your data" },
    { value: `$${STARTING_PRICE}/mo`, label: "Starting price" },
  ],
  howItWorks: {
    title: "The full lifecycle — one platform",
    description: "From fixture counts to final invoice.",
    steps: [
      {
        step: 1,
        title: "Set up your business",
        description:
          "Configure pipe costs by material and diameter (copper, PEX, CPVC), fitting prices, fixture specs, and journeyman/apprentice rates. Build work packages — a bathroom rough-in bundle (supply + drain + vent + fixtures + labor) you reuse on every job.",
        icon: Droplets,
      },
      {
        step: 2,
        title: "Win the work",
        description:
          "Upload plumbing plans, count fixtures, measure pipe runs, and pull in your work packages. Organize by zone — kitchen, bath, utility. Every fitting, every valve, every connection priced. Send a branded proposal the same day.",
        icon: Calculator,
      },
      {
        step: 3,
        title: "Run the job",
        description:
          "Your accepted proposal becomes a project. Track crew hours, log material costs. Service calls — water heater replacements, drain clearing, repairs — tracked alongside project work with visit logging.",
        icon: Wrench,
      },
      {
        step: 4,
        title: "Know your numbers",
        description:
          "Actual costs feed back into your cost database. Compare copper vs PEX profitability. Ask Hank: 'What's my average margin on bathroom rough-ins this year?'",
        icon: DollarSign,
      },
    ],
  },
  features: {
    title: "What you actually get",
    description:
      "Not generic construction software. A platform that understands pipe materials, fixture counts, and plumbing-specific labor.",
    items: [
      {
        icon: Layers,
        title: "Work packages for common rough-ins",
        description:
          "Bathroom rough-in (supply + drain + vent + fixtures + labor), kitchen rough-in (sink + dishwasher + disposal + gas), water heater install (unit + piping + valves + venting + labor).",
        bullets: [
          "Material + labor bundles by pipe type",
          "Production rates for plumbing tasks",
          "Configurable per job",
          "Track usage across estimates",
        ],
      },
      {
        icon: Truck,
        title: "Material pricing by type and diameter",
        description:
          "Track pipe costs by material (copper, PEX, CPVC) and diameter. Fitting prices per connection type. Import from your supply house. Update once — every estimate uses the current number.",
        bullets: [
          "Per-material, per-diameter pricing",
          "Fitting costs by type",
          "Import from supply house spreadsheets",
          "Equipment tracking (camera, jetter)",
        ],
      },
      {
        icon: Wrench,
        title: "Service calls in the same system",
        description:
          "Water heater replacements, drain clearing, leak repairs — service work tracked with visit logging. Same client, same cost database, same labor rates as your project estimates.",
        bullets: [
          "Service management with visit tracking",
          "Cost logging per call",
          "Same client and cost data as projects",
          "Schedule with calendar view",
        ],
      },
      {
        icon: Ruler,
        title: "Blueprint takeoffs for plumbing plans",
        description:
          "Upload plumbing plans, count fixtures, and measure pipe runs directly on screen. Quantities feed into your estimate through work packages.",
        bullets: [
          "Count fixtures on plans",
          "Measure pipe runs by material",
          "Organize by zone",
          "Multi-page plan support",
        ],
      },
    ],
  },
  beforeAfter: {
    title: "The old way vs. the CC way",
    before: [
      { icon: X, text: "Fitting prices from memory" },
      { icon: X, text: "Fixture counts on a legal pad" },
      { icon: X, text: "Service calls tracked on a notepad" },
      { icon: X, text: "No idea if copper vs PEX changes your margin" },
      { icon: X, text: "Proposals that take days to assemble" },
      { icon: X, text: "Crew hours tracked on paper" },
    ],
    after: [
      {
        icon: CheckCircle,
        text: "Material pricing by type, diameter, and supplier",
      },
      { icon: CheckCircle, text: "Fixture counts organized by zone" },
      {
        icon: CheckCircle,
        text: "Service calls and projects in the same system",
      },
      { icon: CheckCircle, text: "Budget vs. actuals on every job" },
      { icon: CheckCircle, text: "Branded proposals sent the same day" },
      { icon: CheckCircle, text: "Time entries with approval workflow" },
    ],
  },
  roi: {
    title: "What changes when your business is connected",
    description:
      "When your material pricing feeds into estimates, your actuals feed back, and your service calls are tracked alongside project work.",
    metrics: [
      {
        value: "Accurate",
        label: "Costs by material type, diameter, and fitting",
      },
      { value: "Faster", label: "Fixture-level bids in hours, not days" },
      {
        value: "Visible",
        label: "Profitability per job — projects and service",
      },
    ],
    ctaText: CTA.PRIMARY,
  },
  faq: {
    title: "Questions plumbing contractors ask",
    items: [
      {
        question: "Does it handle different pipe materials?",
        answer:
          "Yes. Configure separate pricing for copper, PEX, CPVC, and cast iron — by diameter. Each material has its own fittings and connection types in the cost database.",
      },
      {
        question: "Can I track service calls?",
        answer:
          "Yes. Service work is tracked with visit logging, cost tracking, and scheduling. Same client record and cost database as your project estimates.",
      },
      {
        question: "How do work packages work for plumbing?",
        answer:
          "Build reusable bundles — a 'Bathroom Rough-In' package includes supply lines, drain, vent, fixtures, and labor. Pull it into any estimate and adjust for the specific layout.",
      },
      {
        question: "Can I import my supply house pricing?",
        answer:
          "Yes. Import from Excel or CSV. Map pipe, fittings, fixtures, and equipment into your cost database.",
      },
      {
        question: "Can I track crew hours per job?",
        answer:
          "Yes. Time entries with approval workflow. Labor costs logged against the estimate for budget vs. actuals.",
      },
      {
        question: "Can my team use it?",
        answer:
          "Yes. Add estimators, foremen, or office staff. Role-based access controls who sees what.",
      },
    ],
  },
  cta: {
    title: "Run your plumbing business from one platform.",
    description:
      "Material pricing, fixture counts, service calls, invoicing — connected. 7-day free trial.",
    ctaText: CTA.PRIMARY,
    trustIndicators: [
      "7-day free trial",
      "No long-term contracts",
      "Pre-loaded plumbing costs",
      "Cancel anytime",
    ],
  },
};
